import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Link } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, Trophy, Activity, Loader2, ArrowRight } from "lucide-react";

export default function Performance() {
  const { t } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    client.get("/public/performance").then((r) => setData(r.data)).catch(() => setData({ total_trades: 0 }));
  }, []);

  if (!data) return <div className="p-20 text-center"><Loader2 className="inline animate-spin text-[#CCFF00]" /></div>;

  const pos = (data.cumulative_pnl || 0) >= 0;

  return (
    <div data-testid="performance-page" className="max-w-[1400px] mx-auto px-6 py-12">
      <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-mono">Live · Transparent</div>
      <h1 className="font-display text-3xl md:text-5xl font-bold mt-2">{t("perf_title")}</h1>
      <p className="text-white/50 text-sm mt-3 max-w-2xl">{t("perf_sub")}</p>

      {/* Stats */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-px bg-[#222] border border-[#222]">
        <PStat k={t("perf_trades")} v={data.total_trades} testid="perf-total-trades" />
        <PStat k={t("perf_winrate")} v={`${data.win_rate}%`} accent testid="perf-winrate" />
        <PStat k={t("perf_pnl")} v={`${pos ? "+" : ""}$${Number(data.cumulative_pnl || 0).toLocaleString()}`} accent={pos} bad={!pos} testid="perf-pnl" />
        <PStat k={t("perf_open")} v={data.open_positions} testid="perf-open" />
        <PStat k={t("perf_chains")} v={data.chains_scanned} testid="perf-chains" />
      </div>

      {/* Chart */}
      <div className="mt-10 surface p-6" data-testid="perf-chart">
        <div className="font-display text-sm uppercase tracking-widest mb-6 flex items-center gap-2">
          <TrendingUp size={14} className="text-[#CCFF00]" /> {t("perf_chart_title")}
        </div>
        {(data.daily_series || []).length === 0 ? (
          <div className="p-10 text-center text-white/40 text-sm font-mono">{t("perf_no_data")}</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.daily_series}>
              <defs>
                <linearGradient id="pnlGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#CCFF00" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#CCFF00" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1a1a1a" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#222" }} tickLine={false} />
              <YAxis tick={{ fill: "#666", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #222", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }} />
              <Area type="monotone" dataKey="cumulative" name="P&L cumulé ($)" stroke="#CCFF00" strokeWidth={2} fill="url(#pnlGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Best trades */}
      <div className="mt-10 surface">
        <div className="px-6 py-4 border-b border-[#222] font-display text-sm uppercase tracking-widest flex items-center gap-2">
          <Trophy size={14} className="text-[#CCFF00]" /> {t("perf_best")}
        </div>
        {(data.best_trades || []).length === 0 && <div className="p-8 text-center text-white/40 text-sm font-mono">{t("perf_no_data")}</div>}
        {(data.best_trades || []).map((b, i) => (
          <div key={i} className="px-6 py-4 border-b border-[#222] last:border-0 grid grid-cols-12 items-center gap-2" data-testid={`best-trade-${i}`}>
            <div className="col-span-1 font-mono text-xs text-[#CCFF00]">#{i + 1}</div>
            <div className="col-span-4 md:col-span-3 font-display font-semibold flex items-center gap-2 flex-wrap">
              {b.symbol}
              <span className="text-[9px] border border-[#333] text-white/50 px-1.5 py-0.5 font-mono uppercase">{b.chain}</span>
            </div>
            <div className={`col-span-3 md:col-span-2 font-mono text-sm font-bold ${b.pnl_pct >= 0 ? "text-[#CCFF00]" : "text-[#FF3366]"}`}>
              {b.pnl_pct >= 0 ? "+" : ""}{b.pnl_pct}%
            </div>
            <div className="col-span-2 font-mono text-xs text-white/60">{b.pnl_usd >= 0 ? "+" : ""}${b.pnl_usd}</div>
            <div className="hidden md:block col-span-2 font-mono text-[10px] text-white/40">{(b.reason || "").toUpperCase().replace("_", "-")}</div>
            <div className="col-span-2 font-mono text-[10px] text-white/40 text-right">{(b.closed_at || "").slice(0, 10)}</div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-12 border border-[#CCFF00]/30 bg-[#CCFF00]/5 p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="font-display text-xl md:text-2xl font-bold flex items-center gap-3">
            <Activity size={22} className="text-[#CCFF00]" /> {t("perf_cta_title")}
          </div>
          <p className="text-white/50 text-sm mt-2 max-w-xl">{t("perf_cta_sub")}</p>
        </div>
        <Link to="/register" data-testid="perf-cta-button" className="btn-acid !py-3.5 !px-8 flex items-center gap-2 whitespace-nowrap">
          {t("hero_cta")} <ArrowRight size={16} />
        </Link>
      </div>

      <div className="mt-8 text-[10px] text-white/30 font-mono leading-relaxed">
        {t("risk_body")}
      </div>
    </div>
  );
}

function PStat({ k, v, accent, bad, testid }) {
  const cls = bad ? "text-[#FF3366]" : accent ? "text-[#CCFF00]" : "text-white";
  return (
    <div className="bg-[#0A0A0A] p-6" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">{k}</div>
      <div className={`font-display text-2xl font-bold mt-2 ${cls}`}>{v}</div>
    </div>
  );
}
